import html2canvas from "html2canvas";
import React, { useState } from "react";

import { useRace } from "../../contexts/RaceContext";
import { useYear } from "../../contexts/YearContext";
import { getData, getStatesNames, getStatesPopulation } from "../../data";
import Card from "../Card";
import MapChart from "../MapChart2";
import SelectRace from "../SelectRace";
import SelectYear from "../SelectYear";
import styles from "./MapGraphic.module.css";
import ReactTooltip from "react-tooltip";

function stringToDivs(string) {
  return string.split(" ").map((w) => <div className={styles.word}>{w}</div>);
}

function getDataFiltered(race, year) {
  let data = getData();

  data = data.filter((d) => new Date(d.date).getFullYear() === year);

  if (race !== "all") {
    data = data.filter((d) => d.race === race);
  }

  return data;
}

const getPerMillion = (population, deaths) => {
  const a = population;
  const b = deaths;
  const c = 1000000;

  return (c * b) / a;
};

const MapGraphic = () => {
  const printRef = React.useRef();

  const handleDownloadImage = async () => {
    const element = printRef.current;
    const canvas = await html2canvas(element);

    const data = canvas.toDataURL("image/jpg");
    const link = document.createElement("a");

    if (typeof link.download === "string") {
      link.href = data;
      link.download = "image.jpg";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(data);
    }
  };

  function getColor(number, max) {
    const COLORS = [
      "#daf6ff",
      "#bfefff",
      "#a3e6ff",
      "#88defe",
      "#6dd4fe",
      "#52cafd",
      "#37c0fc",
      "#1db5fb",
      "#05a8f7",
      "#0593db",
      "#057fc0",
      "#056ba4",
      "#045889",
      "#04466e",
      "#033453",
    ];

    const a = max;
    const b = 10;
    const c = number;

    const color = Math.ceil((c * b) / a);

    return COLORS[color];
  }

  const downLoadImage = async () => {
    await handleDownloadImage();
  };

  const race = useRace();
  const year = useYear();
  const isCurrentYear = year === new Date().getFullYear();
  const data = getDataFiltered(race, year);

  const statesPopulation = getStatesPopulation();
  const statesNames = getStatesNames();

  const counts = {};

  data.forEach((x) => {
    if (typeof counts[x.state] !== "undefined") {
      counts[x.state]++;
    } else {
      counts[x.state] = 1;
    }
  });

  let max = 0;
  const statesData = {};

  for (let index = 0; index < Object.keys(counts).length; index++) {
    const current = Object.keys(counts)[index];
    const statePopulation = statesPopulation.find(
      (s) => s.name === statesNames[current]
    );
    const population = statePopulation.population.replace(/,/g, "");
    const perMillion = getPerMillion(population, counts[current]);

    max = max < perMillion ? perMillion : max;
    statesData[current] = {
      perMillion,
    };
  }

  const count = Object.keys(statesData).length;
  const maxPerMillion = Math.floor(max);

  for (let index = 0; index < Object.keys(statesData).length; index++) {
    const current = Object.keys(statesData)[index];

    statesData[current].fill = getColor(
      statesData[current].perMillion,
      maxPerMillion
    );
  }

  const [content, setContent] = useState("");
  const [country, setCountry] = useState("");

  const message = `Police ${isCurrentYear ? "have" : ""} Killed ${race === "all" ? "" : "people "} in ${count} states in ${year} ${isCurrentYear ? "so far" : ""}`

  return (
    <div className={styles.container} ref={printRef}>
      <Card
        downLoadImage={downLoadImage}
        message={message}
      >
        <div className={styles.text}>
          {stringToDivs(`Police ${isCurrentYear ? "have" : ""} Killed`)}
          <div className={styles.word}>
            <SelectRace withLabel={false} small={true} />
          </div>
          {stringToDivs(
            `${race === "all" ? "" : "people "} in ${count} states in`
          )}
          <div className={styles.word}>
            <SelectYear withLabel={false} small={true} />
          </div>
          {stringToDivs(isCurrentYear ? "so far" : "")}
        </div>
        <div className={styles.subtitle}>
          {stringToDivs("Fewer police killings")}
          <div className={styles.svg}>
            <svg
              width="103"
              height="15"
              viewBox="0 0 103 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                width="103"
                height="15"
                fill="url(#paint0_linear_18_3405)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_18_3405"
                  x1="103"
                  y1="8.00002"
                  x2="-4.45152e-08"
                  y2="8.00002"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#033453" />
                  <stop offset="1" stop-color="#DAF6FF" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          {stringToDivs("More police killings")}
        </div>
        <div className={styles.content}>
          <div>
            <MapChart
              statesData={statesData}
              setTooltipContent={setContent}
              setTooltipCountry={setCountry}
            />
            {!!content && (
              <ReactTooltip place="bottom" type="dark" effect="float">
                <div>{`${country}, ${year}`}</div>
                {`${content} people killed per million by police`}
              </ReactTooltip>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MapGraphic;
